import React, { useState, useEffect } from 'react'
import {
    Box,
    Typography,
    Button,
    Container,
    IconButton,
    useTheme,
    useMediaQuery,
    Modal,
    Backdrop,
    Fade
} from '@mui/material'
import { ArrowBack, Close, PlayCircle } from '@mui/icons-material'
import _ from 'lodash'
import ReactPlayer from 'react-player'
import { motion } from 'framer-motion'
import Particles from 'react-tsparticles'
import { loadSlim } from 'tsparticles-slim'
import { useLanguage } from '../context/LanguageContext'
import { useNavigate, useLocation } from 'react-router-dom'

const MembershipInfoPage = () => {
    const theme = useTheme()
    const { t } = useLanguage()
    const navigate = useNavigate()
    const { state } = useLocation()
    const [imageError, setImageError] = useState({})
    const [openModal, setOpenModal] = useState(false)
    const [videoError, setVideoError] = useState(false)
    const [playingVideo, setPlayingVideo] = useState(false)
    const [selectedImage, setSelectedImage] = useState(null)
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'))
    const [membership, setMembership] = useState({
        name: '',
        images: [],
        video: '',
        videoThumbnail: ''
    })

    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    useEffect(() => {
        if (state?.package) {
            setMembership({
                name: state.package.name || '',
                images: state.package.images || [],
                video: state.package.videoUrl || '',
                registrationUrl: state.package.registrationUrl || '',
                videoThumbnail: state.package.videoThumbnail || '',
                promotion: state.package.promotion
            })
        }
    }, [state])

    const particlesInit = async (engine) => {
        await loadSlim(engine)
    }

    const handleBack = () => navigate(-1)
    const handleImageClick = (img) => {
        setSelectedImage(img)
        setOpenModal(true)
    }
    const handleCloseModal = () => setOpenModal(false)
    const handleImageError = (index) => {
        setImageError(prev => ({ ...prev, [index]: true }))
    }
    const handleVideoError = () => {
        setVideoError(true)
        setPlayingVideo(false)
    }

    const getColumns = () => {
        if (isMobile) return 3  // Changed from 1 to 3 for mobile
        if (isTablet) return 2
        return 4
    }

    const PromotionBanner = ({ promotion }) => {
        const theme = useTheme()
        const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

        return (
            <motion.div
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.5 }}
            >
                <Box sx={{
                    backgroundColor: 'rgba(255, 215, 0, 0.1)',
                    border: '2px solid rgba(255, 215, 0, 0.3)',
                    borderRadius: '12px',
                    p: isMobile ? 2 : 3,
                    mb: 4,
                    position: 'relative',
                    overflow: 'hidden',
                    '&:before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background: 'linear-gradient(90deg, #FFD700, #FFA500, #FFD700)',
                        animation: 'shimmer 3s infinite linear',
                    }
                }}>
                    <Typography variant="h6" sx={{
                        color: '#FFD700',
                        fontWeight: 'bold',
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center'
                    }}>
                        ✨ {promotion.title} ✨
                    </Typography>
                    <Box component="ul" sx={{
                        pl: isMobile ? 2 : 3,
                        mb: 0,
                        '& li': {
                            mb: 1,
                            color: theme.palette.common.white,
                            fontSize: isMobile ? '0.9rem' : '1rem'
                        }
                    }}>
                        {promotion.items.map((item, index) => (
                            <Box component="li" key={index} sx={{
                                fontWeight: item.startsWith('🎁') || item.startsWith('🉑') ? 'bold' : 'normal',
                                color: item.startsWith('🎁') ? '#FFD700' : item.startsWith('🉑') ? '#00FFAA' : 'inherit',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                {item.startsWith('~') ? (
                                    <>
                                        <ArrowBack sx={{ fontSize: '1rem', mr: 1, transform: 'rotate(180deg)' }} />
                                        {item.substring(1)}
                                    </>
                                ) : (
                                    item
                                )}
                            </Box>
                        ))}
                    </Box>
                </Box>
            </motion.div>
        )
    }

    return (
        <Box sx={{
            position: 'relative',
            overflow: 'hidden',
            minHeight: '100vh',
            background: 'radial-gradient(circle at 10% 20%, rgba(25,25,35,0.96) 0%, rgba(10,10,20,1) 90%)',
            color: theme.palette.common.white
        }}>
            <Particles
                id="tsparticles"
                init={particlesInit}
                options={{
                    fpsLimit: 60,
                    interactivity: {
                        events: {
                            onHover: {
                                enable: true,
                                mode: "repulse",
                            },
                        },
                        modes: {
                            repulse: {
                                distance: 100,
                                duration: 0.4,
                            },
                        },
                    },
                    particles: {
                        color: {
                            value: theme.palette.primary.main,
                        },
                        links: {
                            color: theme.palette.secondary.main,
                            distance: 150,
                            enable: true,
                            opacity: 0.3,
                            width: 1,
                        },
                        collisions: {
                            enable: true,
                        },
                        move: {
                            direction: "none",
                            enable: true,
                            outMode: "bounce",
                            random: false,
                            speed: 1,
                            straight: false,
                        },
                        number: {
                            density: {
                                enable: true,
                                value_area: 800,
                            },
                            value: 60,
                        },
                        opacity: {
                            value: 0.3,
                        },
                        shape: {
                            type: "circle",
                        },
                        size: {
                            random: true,
                            value: 3,
                        },
                    },
                    detectRetina: true,
                }}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    zIndex: 0,
                    opacity: 0.7
                }}
            />

            <Container maxWidth="xl" sx={{
                py: isMobile ? 4 : 6,
                px: isMobile ? 2 : 4,
                position: 'relative',
                zIndex: 1
            }}>
                {/* Header */}
                <motion.div
                    initial={{ y: -50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.8 }}
                >
                    <Box sx={{
                        mb: isMobile ? 3 : 4,
                        display: 'flex',
                        alignItems: 'center',
                        position: 'relative'
                    }}>
                        <motion.div whileHover={{ scale: 1.1 }}>
                            <IconButton onClick={handleBack} sx={{
                                mr: 2,
                                color: theme.palette.common.white,
                                backgroundColor: 'rgba(255,255,255,0.1)',
                                '&:hover': {
                                    backgroundColor: 'rgba(255,255,255,0.2)'
                                }
                            }}>
                                <ArrowBack />
                            </IconButton>
                        </motion.div>
                        <Typography variant={'h4'} sx={{
                            fontWeight: 'bold',
                            background: 'linear-gradient(90deg, #FFFFFF 0%, #E0E0E0 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                        }}>
                            {membership.name}{t('membershipInfoPage.membership')}
                        </Typography>
                    </Box>
                </motion.div>

                {/* Promotional Banner - Add this right after the header */}
                {membership.promotion && (
                    <PromotionBanner promotion={membership.promotion} />
                )}

                {membership.images?.length > 0 ? (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.5, staggerChildren: 0.1 }}
                    >
                        <Box sx={{
                            mb: isMobile ? 4 : 6,
                            display: 'grid',
                            gridTemplateColumns: `repeat(${getColumns()}, 1fr)`,
                            gap: isMobile ? '8px' : '24px'  // Reduced gap on mobile
                        }}>
                            {membership.images.map((item, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ scale: 0.9, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                    whileHover={{ scale: 1.03 }}
                                >
                                    <Box sx={{
                                        position: 'relative',
                                        borderRadius: '8px',  // Slightly smaller radius on mobile
                                        overflow: 'hidden',
                                        aspectRatio: '1/1',
                                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',  // Smaller shadow on mobile
                                    }}>
                                        {imageError[index] ? (
                                            <Box sx={{
                                                width: '100%',
                                                height: '100%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                backgroundColor: 'rgba(0,0,0,0.1)',
                                                color: 'white'
                                            }}>
                                                <Typography variant="caption">{t('membershipInfoPage.imageNotAvailable')}</Typography>
                                            </Box>
                                        ) : (
                                            <img
                                                src={item.img}
                                                alt={item.title || `Image ${index + 1}`}
                                                loading="lazy"
                                                onError={() => handleImageError(index)}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    cursor: 'pointer',
                                                    transition: 'transform 0.5s ease'
                                                }}
                                                onClick={() => handleImageClick(item.img)}
                                            />
                                        )}
                                        <Box className="image-overlay" sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            background: 'linear-gradient(to bottom, rgba(0,0,0,0) 60%, rgba(0,0,0,0.7) 100%)',
                                            opacity: isMobile ? 1 : 0,
                                            transition: 'opacity 0.3s ease'
                                        }} />
                                        <Typography
                                            className="image-title"
                                            variant={isMobile ? 'caption' : 'subtitle1'}
                                            sx={{
                                                position: 'absolute',
                                                top: '50%',
                                                left: '50%',
                                                transform: 'translate(-50%, -50%)',
                                                color: 'white',
                                                fontWeight: 'bold',
                                                opacity: 1, // Always visible
                                                textAlign: 'center',
                                                width: '90%',
                                                zIndex: 1,
                                                textShadow: '0 2px 4px rgba(0,0,0,0.8)',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                // Blurry background
                                                backgroundColor: 'rgba(0,0,0,0.4)',
                                                backdropFilter: 'blur(4px)',
                                                borderRadius: '4px',
                                                px: 1,
                                                py: 0.5
                                            }}
                                        >
                                            {item.title || `Image ${index + 1}`}
                                        </Typography>
                                    </Box>
                                </motion.div>
                            ))}
                        </Box>
                    </motion.div>
                ) : (
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '200px',
                        backgroundColor: 'rgba(255,255,255,0.05)',
                        borderRadius: '12px',
                        mb: 4
                    }}>
                        <Typography variant="h6">{t('membershipInfoPage.noImagesAvailable')}</Typography>
                    </Box>
                )}

                {/* Video Section */}
                {membership.video && !videoError ? (
                    <Box sx={{
                        mt: 'auto',
                        mb: 2
                    }}>
                        <Typography variant={isMobile ? 'h6' : 'h5'} sx={{
                            fontWeight: 'bold',
                            mb: 3,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            color: theme.palette.common.white,
                            '&:before, &:after': {
                                content: '""',
                                flex: 1,
                                height: '1px',
                                background: `linear-gradient(to right, transparent, ${theme.palette.primary.main}, transparent)`,
                                mr: 2,
                                ml: 2
                            }
                        }}>
                            {t('membershipInfoPage.videoPresentation')}
                        </Typography>

                        <Box sx={{
                            borderRadius: '16px',
                            overflow: 'hidden',
                            boxShadow: '0 20px 50px rgba(0, 0, 0, 0.5)',
                            aspectRatio: '16/9',
                            border: '1px solid rgba(255,255,255,0.1)',
                        }}>
                            {!playingVideo ? (
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        position: 'relative',
                                        cursor: 'pointer',
                                        '&:hover .play-button': {
                                            transform: 'scale(1.1) translate(-50%, -50%)',
                                            opacity: 1
                                        },
                                        '&:hover .video-overlay': {
                                            opacity: 0.7
                                        }
                                    }}
                                    onClick={() => setPlayingVideo(true)}
                                >
                                    {membership.videoThumbnail ? (
                                        <img
                                            src={membership.videoThumbnail}
                                            alt="Video thumbnail"
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover'
                                            }}
                                        />
                                    ) : (
                                        <Box sx={{
                                            width: '100%',
                                            height: '100%',
                                            backgroundColor: 'rgba(0,0,0,0.2)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'white'
                                        }}>
                                            <Typography>Video thumbnail not available</Typography>
                                        </Box>
                                    )}
                                    <Box className="video-overlay" sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                                        opacity: 0.4,
                                        transition: 'opacity 0.3s ease'
                                    }} />
                                    <Box
                                        className="play-button"
                                        sx={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            transition: 'all 0.3s ease',
                                            color: 'white',
                                            fontSize: '5rem',
                                            opacity: 0.9,
                                            filter: 'drop-shadow(0 0 10px rgba(0,0,0,0.5))'
                                        }}
                                    >
                                        <PlayCircle fontSize="inherit" />
                                    </Box>
                                </Box>
                            ) : (
                                <ReactPlayer
                                    url={membership.video}
                                    width="100%"
                                    height="100%"
                                    controls
                                    playing
                                    onError={handleVideoError}
                                    onEnded={() => setPlayingVideo(false)}
                                    config={{
                                        file: {
                                            attributes: {
                                                controlsList: 'nodownload'
                                            }
                                        }
                                    }}
                                />
                            )}
                        </Box>
                    </Box>
                ) : videoError && (
                    <Box sx={{
                        mt: 4,
                        mb: 4,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '200px',
                        backgroundColor: 'rgba(255,0,0,0.1)',
                        borderRadius: '12px'
                    }}>
                        <Typography variant="h6" color="error">{t('membershipInfoPage.videoFailedToLoad')}</Typography>
                    </Box>
                )}

                {/* CTA Button */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mt: 4,
                    mb: 4
                }}>
                    <Button
                        variant="contained"
                        size={isMobile ? 'medium' : 'large'}
                        color="secondary"
                        href={membership.registrationUrl || '#'}  // Fallback to '#' if undefined
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => {
                            console.log(membership)
                        }}
                        sx={{
                            px: isMobile ? 4 : 6,
                            py: isMobile ? 1.5 : 2,
                            borderRadius: '50px',
                            fontWeight: 'bold',
                            fontSize: isMobile ? '1rem' : '1.1rem',
                            textTransform: 'uppercase',
                            letterSpacing: '2px',
                            boxShadow: '0 4px 20px rgba(255, 215, 0, 0.4)',
                            background: 'linear-gradient(45deg, #FFD700 0%, #FFA500 100%)',
                            color: '#000',
                            '&:hover': {
                                boxShadow: '0 8px 30px rgba(255, 215, 0, 0.6)',
                                transform: 'translateY(-3px)',
                                background: 'linear-gradient(45deg, #FFD700 0%, #FFA500 100%)'
                            },
                            transition: 'all 0.3s ease'
                        }}
                    >
                        {t('membershipInfoPage.subscribe')}{membership.name}{t('membershipInfoPage.membership')}
                    </Button>
                </Box>

                {/* Image Modal */}
                <Modal
                    open={openModal}
                    onClose={handleCloseModal}
                    closeAfterTransition
                    BackdropComponent={Backdrop}
                    BackdropProps={{ timeout: 500 }}
                >
                    <Fade in={openModal}>
                        <Box sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: isMobile ? '95%' : '90%',
                            maxWidth: '1200px',
                            maxHeight: '90vh',
                            outline: 'none',
                            bgcolor: 'background.paper',
                            boxShadow: '0 30px 60px rgba(0, 0, 0, 0.5)',
                            p: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            borderRadius: isMobile ? '8px' : '16px',
                            overflow: 'hidden'
                        }}>
                            <IconButton
                                onClick={handleCloseModal}
                                sx={{
                                    position: 'absolute',
                                    right: isMobile ? 8 : 16,
                                    top: isMobile ? 8 : 16,
                                    color: 'white',
                                    backgroundColor: 'rgba(0,0,0,0.5)',
                                    zIndex: 1,
                                    '&:hover': {
                                        backgroundColor: 'rgba(0,0,0,0.7)'
                                    }
                                }}
                            >
                                <Close />
                            </IconButton>
                            {selectedImage ? (
                                <img
                                    src={selectedImage}
                                    alt="Enlarged preview"
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '90vh',
                                        objectFit: 'contain',
                                    }}
                                    onError={(e) => {
                                        e.target.onerror = null
                                        e.target.src = 'data:image/svg+xmlcharset=UTF-8,%3Csvg%20width%3D%22800%22%20height%3D%22600%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%20800%20600%22%20preserveAspectRatio%3D%22none%22%3E%3Cdefs%3E%3Cstyle%20type%3D%22text%2Fcss%22%3E%23holder_18945b7b3e7%20text%20%7B%20fill%3A%23AAAAAA%3Bfont-weight%3Abold%3Bfont-family%3AArial%2C%20Helvetica%2C%20Open%20Sans%2C%20sans-serif%2C%20monospace%3Bfont-size%3A40pt%20%7D%20%3C%2Fstyle%3E%3C%2Fdefs%3E%3Cg%20id%3D%22holder_18945b7b3e7%22%3E%3Crect%20width%3D%22800%22%20height%3D%22600%22%20fill%3D%22%23EEEEEE%22%3E%3C%2Frect%3E%3Cg%3E%3Ctext%20x%3D%22285.9140625%22%20y%3D%22320.7%22%3EImage%20not%20available%3C%2Ftext%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E'
                                    }}
                                />
                            ) : (
                                <Box sx={{
                                    width: '100%',
                                    height: '300px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'rgba(0,0,0,0.1)'
                                }}>
                                    <Typography>{t('membershipInfoPage.imageNotAvailable')}</Typography>
                                </Box>
                            )}
                        </Box>
                    </Fade>
                </Modal>
            </Container>
        </Box>
    )
}

export default MembershipInfoPage